import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

export interface ColumnConfig<T = any> {
    key: keyof T;
    label: string;
    className?: string;
    formatter?: (value: any) => string | number | React.ReactNode;
    sortable?: boolean;
}

export interface DynamicTableProps<T = any> {
    data: T[];
    columns?: ColumnConfig<T>[];
    sortable?: boolean;
    className?: string;
}

export default function DynamicTable<T extends Record<string, any>>({
    data,
    columns,
    sortable = true,
    className = '',
}: DynamicTableProps<T>) {
    const [sortConfig, setSortConfig] = useState<{
        key: keyof T | null;
        direction: 'asc' | 'desc';
    }>({ key: null, direction: 'asc' });

    // Auto-generate columns if not provided
    const resolvedColumns = useMemo(() => {
        if (columns) return columns;

        if (data.length === 0) return [];

        const firstItem = data[0];
        return Object.keys(firstItem)
            .filter(key => {
                const value = firstItem[key];
                return typeof value === 'string' || typeof value === 'number';
            })
            .map(key => ({
                key: key as keyof T,
                label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
                sortable: true,
            }));
    }, [data, columns]);

    // Sort data based on current sort configuration
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key!];
            const bValue = b[sortConfig.key!];

            if (aValue === undefined || bValue === undefined) return 0;

            let comparison = 0;
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                comparison = aValue.localeCompare(bValue);
            } else {
                comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            }

            return sortConfig.direction === 'desc' ? -comparison : comparison;
        });
    }, [data, sortConfig]);

    const handleSort = (key: keyof T) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const formatValue = (value: any, column: ColumnConfig<T>) => {
        if (column.formatter) {
            const formatted = column.formatter(value);
            // If formatter returns a ReactNode, render it directly
            if (React.isValidElement(formatted)) {
                return formatted;
            }
            // Otherwise, return as string/number
            return formatted;
        }
        return value;
    };

    if (data.length === 0) return null;

    return (
        <div className={`max-w-full border-solid border-1 rounded-xs ${className}`}>
            <Table>
                <TableHeader>
                    <TableRow>
                        {resolvedColumns.map((column) => (
                            <TableHead
                                key={String(column.key)}
                                className={`select-none ${column.className || ''} ${sortable && column.sortable !== false
                                    ? 'cursor-pointer hover:bg-gray-50'
                                    : ''
                                    }`}
                                onClick={() => {
                                    if (sortable && column.sortable !== false) {
                                        handleSort(column.key);
                                    }
                                }}
                            >
                                <div className={`flex items-center gap-1 ${column.className?.includes('text-right') ? 'justify-end' : ''
                                    }`}>
                                    {sortConfig.key === column.key && (
                                        <span className="text-xs">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                    {column.label}
                                </div>
                            </TableHead>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sortedData.map((item, index) => (
                        <TableRow key={index}>
                            {resolvedColumns.map((column) => (
                                <TableCell
                                    key={String(column.key)}
                                    className={column.className}
                                >
                                    <span className="p">
                                        {formatValue(item[column.key], column)}
                                    </span>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
} 